/**
 * FRIDGE-1434 — head pages lead with the singular a shopper types.
 *
 * Measured (Semrush, Google US, 10/05/2026 tracking and the 07/08/2026
 * positions export): "kegerator" 27,100 searches a month against
 * "kegerators" 1,900; "freezer" 74,000 against "freezers" 12,100; and
 * neither /kegerators nor /collections/freezers held a top-100 position for
 * the singular or the plural, while both pages are live, indexable and
 * self-canonical. Both led with the plural in the title and the H1.
 *
 * This module is the ONE place the two head pages take their H1, their
 * <title> keyword and benefit, and the answer-first sentence's noun from.
 * The hub route (app/[hub-slug]/page.tsx, hub-specialty.tsx) and the
 * collection route (app/collections/[slug]/collection-page-render.tsx and
 * its client) read it; nothing retypes the words. Titles compose through
 * composePageTitle, so the shared length policy (FRIDGE-1138) still governs.
 *
 * The answer-first sentence states the page's own numbers (the model count
 * and the median lowest price the page already computes), never a retyped
 * figure (the FRIDGE-1435 class).
 */
import { composePageTitle } from '@/lib/seo/page-title'

export interface HeadTermHeadline {
  /** The singular noun the sentence and the keyword lead with ("Kegerator"). */
  term: string
  /** The H1. Leads with the singular; the ':' qualifier names the page. */
  h1: string
  /** The <title> keyword. */
  keyword: string
  /** The <title> benefit (the searcher's goal on this page). */
  benefit: string
}

const HUBS: Record<string, HeadTermHeadline> = {
  kegerators: {
    term: 'Kegerator',
    h1: 'Kegerator Prices & Deals: Keg Fridges & Beer Dispensers',
    keyword: 'Kegerator Prices & Deals',
    benefit: 'Keg Fridges & Beer Dispensers',
  },
}

const COLLECTIONS: Record<string, HeadTermHeadline> = {
  freezers: {
    term: 'Freezer',
    h1: 'Freezer Prices: Chest, Upright & Compact Compared',
    keyword: 'Freezer Prices',
    benefit: 'Chest, Upright & Compact Compared',
  },
}

/** The head-term headline for a hub page (/<slug>), or null. */
export function hubHeadline(slug: string): HeadTermHeadline | null {
  return HUBS[slug] ?? null
}

/** The head-term headline for a collection page (/collections/<slug>), or null. */
export function collectionHeadline(slug: string): HeadTermHeadline | null {
  return COLLECTIONS[slug] ?? null
}

/** Every head-term headline, for the tests and the title census. */
export function allHeadTermHeadlines(): Array<{ route: string; headline: HeadTermHeadline }> {
  return [
    ...Object.entries(HUBS).map(([slug, headline]) => ({ route: `/${slug}`, headline })),
    ...Object.entries(COLLECTIONS).map(([slug, headline]) => ({ route: `/collections/${slug}`, headline })),
  ]
}

/** The page <title> under the shared policy: keyword | benefit | Fridge.com. */
export function headTermTitle(headline: HeadTermHeadline): string {
  return composePageTitle({ keyword: headline.keyword, benefit: headline.benefit })
}

export interface HeadTermIntroFacts {
  term: string
  /** Tracked models on the page; a floor when `countIsFloor` is true. */
  models: number
  countIsFloor?: boolean
  /** Median lowest tracked price, dollars (null when unpublished). */
  medianPrice: number | null
  minPrice?: number | null
  maxPrice?: number | null
}

function dollars(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`
}

/**
 * The answer-first sentence a head page opens with. It names the singular,
 * states the count and the median, and says what the page does. Null when
 * there is nothing measured to state, so the caller keeps its own fallback.
 */
export function headTermIntro(facts: HeadTermIntroFacts): string | null {
  if (!(facts.models > 0)) return null
  const noun = facts.term.toLowerCase()
  const plural = `${noun}s`
  const count = `${facts.countIsFloor ? 'over ' : ''}${facts.models.toLocaleString('en-US')}`
  const head = `${facts.term} prices:`
  if (facts.medianPrice == null || !(facts.medianPrice > 0)) {
    return `${head} ${count} ${facts.models === 1 && !facts.countIsFloor ? noun : plural} tracked by Fridge.com, with prices compared across major online appliance retailers.`
  }
  const range =
    facts.minPrice != null && facts.maxPrice != null && facts.maxPrice > facts.minPrice
      ? `, with models from ${dollars(facts.minPrice)} to ${dollars(facts.maxPrice)}`
      : ''
  return `${head} the median lowest price across ${count} ${plural} tracked by Fridge.com is ${dollars(facts.medianPrice)}${range}. Compare prices from major online appliance retailers.`
}
